import ModalContext from 'contexts/ModalContext'
import React, { MouseEventHandler, useCallback, useContext, useMemo } from 'react'
import BusinessTravellerHotelCreditsOverBudgetInfoModal from './BusinessTravellerOfferHotelCreditsOverBudgetInfoModal'
import useBusinessTravellerOfferCreditFetcher from 'businessTraveller/hooks/useBusinessTravellerOfferCreditFetcher'
import PriceRowBusinessTravellerCredit from 'LuxKit/PricePoints/BusinessTraveller/PriceRowBusinessTravellerCredit'
import PriceRowBusinessTravellerExceededBudget from 'LuxKit/PricePoints/BusinessTraveller/PriceRowBusinessTravellerExceededBudget'
import BusinessTravellerOfferCreditsInfoModal from './BusinessTravellerOfferCreditsInfoModal'
import PriceRowBusinessTravellerUnderBudget from 'LuxKit/PricePoints/BusinessTraveller/PriceRowBusinessTravellerUnderBudget'
import BusinessTravellerOfferCreditsUnderBudgetInfoModal from './BusinessTravellerOfferCreditsUnderBudgetInfoModal'
import BusinessTravellerPolicyDebugButton from 'businessTraveller/components/policy-debug/BusinessTravellerPolicyDebugButton'
import { useItemPolicyShadowFetcher } from 'businessTraveller/hooks/itemPolicy/useItemPolicyShadowFetcher'
import { generateItemPolicyOfferCreditKey } from 'businessTraveller/utils/generateItemPolicyCalculationKey'
import { getLocationDataFromOffer } from 'businessTraveller/utils/getLocationDataFromOffer'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectSelectedTravellerEmployeeIds } from 'businessTraveller/selectors/itemPolicy/selectSelectedTravellerEmployeeIds'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'

type SupportedOffer =
  App.Offer |
  App.OfferSummary |
  App.BedbankOffer |
  App.BedbankOfferSummary

type Props = {
  numberOfNights?: number
  offer: SupportedOffer
  totalCost: number
  type: 'exact' | 'estimate'
  roomCount?: number
}

function BusinessTravellerOfferCreditsTextLink(props: Props) {
  const {
    offer,
    totalCost,
    numberOfNights = 1,
    type,
    roomCount,
  } = props
  const showModal = useContext(ModalContext)

  const { currentCurrency: currencyCode } = useContext(GeoContext)
  const employeeIds = useAppSelector(selectSelectedTravellerEmployeeIds)
  const businessId = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.business?.id)
  const itemPolicyCalculatedCredits = useAppSelector(state => state.businessTraveller.itemPolicyCalculatedCredits)

  const { offerCreditData, offerCreditKey } = useBusinessTravellerOfferCreditFetcher({
    offer,
    numberOfNights,
    cost: totalCost,
    roomCount,
  })

  const { itemPolicyCacheKey, shadowItem } = useMemo(() => {
    const { latitude, longitude } = getLocationDataFromOffer(offer).location
    const hotelItem: App.BusinessTraveller.ToBeCalculatedItemPolicyHotelItem = {
      itemPolicyType: 'HOTEL',
      key: `hotel_${offer.id}_${totalCost}_${numberOfNights}_${roomCount ?? 1}`,
      cost: totalCost,
      currencyCode,
      numberOfNights,
      roomCount: roomCount ?? 1,
      travellerCount: 1,
      location: { latitude, longitude },
    }
    return {
      itemPolicyCacheKey: generateItemPolicyOfferCreditKey(hotelItem, offer.id, employeeIds),
      shadowItem: hotelItem,
    }
  }, [currencyCode, employeeIds, numberOfNights, offer, roomCount, totalCost])

  const itemPolicyData = itemPolicyCalculatedCredits?.[itemPolicyCacheKey]

  useItemPolicyShadowFetcher({
    enabled: true,
    businessId,
    cacheKey: itemPolicyCacheKey,
    item: employeeIds.length > 0 ? shadowItem : undefined,
    employeeIds,
    vertical: 'hotel',
    surface: 'offer_credit',
    legacyData: offerCreditData,
    itemPolicyData,
  })

  const showCreditInfoModal = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()

    showModal(<BusinessTravellerOfferCreditsInfoModal />)
  }, [showModal])

  const showOverBudgetInfoModal = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()

    showModal(<BusinessTravellerHotelCreditsOverBudgetInfoModal numberOfNights={numberOfNights} />)
  }, [numberOfNights, showModal])

  const showUnderBudgetInfoModal = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()

    showModal(<BusinessTravellerOfferCreditsUnderBudgetInfoModal />)
  }, [showModal])

  const showUnderBudgetInfo = offerCreditData?.status === 'success' && offerCreditData?.budgetCompliance === 'under_budget'
  const showOverBudgetInfo = offerCreditData?.status === 'success' && offerCreditData?.budgetCompliance === 'over_budget'

  return <Group direction="horizontal" gap={8} verticalAlign="center">
    {showUnderBudgetInfo && offerCreditData?.creditValue > 0 && <PriceRowBusinessTravellerCredit
      size="M"
      type={type}
      creditValue={offerCreditData.creditValue}
      onClick={showCreditInfoModal}
    />}

    {showUnderBudgetInfo && offerCreditData?.creditValue === 0 && <PriceRowBusinessTravellerUnderBudget
      underPercentage={offerCreditData.budgetComparison}
      onClick={showUnderBudgetInfoModal}
    />}

    {showOverBudgetInfo && <PriceRowBusinessTravellerExceededBudget
      exceedingPercentage={offerCreditData.budgetComparison}
      onClick={showOverBudgetInfoModal}
    />}
    <BusinessTravellerPolicyDebugButton
      source="legacy"
      vertical="hotel"
      surface="offer_credit"
      primaryData={offerCreditData}
      comparisonData={itemPolicyData}
      cacheKey={offerCreditKey}
      employeeIds={employeeIds}
    />
  </Group>
}

export default BusinessTravellerOfferCreditsTextLink
